import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  BookingBackendListModel,
  BookingBackendModel,
  BookingCreateUpdateModel,
  BookingListModel,
  BookingModel,
  ContractRevocationPreviewBackendModel,
  ContractRevocationPreviewModel,
  ContractRevocationRequestModel,
} from '../models/booking.model';
import { CouponService } from './coupon.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private couponService: CouponService
  ) {}

  public createBooking(booking: BookingCreateUpdateModel) {
    const formData = new FormData();
    formData.append('mode', 'createBooking');
    formData.append(
      'id_seminar_date_series',
      booking.id_seminar_date_series.toString()
    );

    formData.append('participants', JSON.stringify(booking.participants));
    formData.append('invoice_address', JSON.stringify(booking.invoice_address));
    booking.id_coupons &&
      formData.append('id_coupons', JSON.stringify(booking.id_coupons));
    formData.append('total_price', booking.total_price.toString());
    formData.append('discounted_price', booking.discounted_price.toString());
    formData.append('payment_method', booking.payment_method);

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: {
          booking_id: number;
          paypal_response?: any;
        };
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => response.data),
        catchError(error => throwError(() => error))
      );
  }

  public updateBooking(booking: BookingCreateUpdateModel) {
    const formData = new FormData();
    formData.append('mode', 'updateBooking');
    formData.append('id_booking', booking.id.toString());
    formData.append(
      'id_seminar_date_series',
      booking.id_seminar_date_series.toString()
    );

    formData.append('participants', JSON.stringify(booking.participants));
    formData.append('invoice_address', JSON.stringify(booking.invoice_address));
    booking.id_coupons &&
      formData.append('id_coupons', JSON.stringify(booking.id_coupons));
    formData.append('total_price', booking.total_price.toString());
    formData.append('discounted_price', booking.discounted_price.toString());
    formData.append('payment_method', booking.payment_method);

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: {
          booking_id: number;
          paypal_response?: any;
        };
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => response.data),
        catchError(error => throwError(() => error))
      );
  }

  public createWaitlistBooking(booking: BookingCreateUpdateModel) {
    const formData = new FormData();
    formData.append('mode', 'createWaitlistBooking');
    formData.append(
      'id_seminar_date_series',
      booking.id_seminar_date_series.toString()
    );

    formData.append('participants', JSON.stringify(booking.participants));
    formData.append('invoice_address', JSON.stringify(booking.invoice_address));
    booking.id_coupons &&
      formData.append('id_coupons', JSON.stringify(booking.id_coupons));
    formData.append('total_price', booking.total_price.toString());
    formData.append('discounted_price', booking.discounted_price.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: {
          booking_id: number;
        };
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => response.data),
        catchError(error => {
          return throwError(() => error);
        })
      );
  }

  public removePayPalBooking(bookingId: number) {
    const formData = new FormData();
    formData.append('mode', 'removePayPalBooking');
    formData.append('id_booking', bookingId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return response;
          } else {
            throw new Error(response.message);
          }
        }),
        catchError(() => {
          return throwError(() => new Error('Booking removal failed'));
        })
      );
  }

  public confirmPaypalBooking(bookingId: number) {
    const formData = new FormData();
    formData.append('mode', 'confirmPaypalBooking');
    formData.append('id_booking', bookingId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return response;
          } else {
            throw new Error(response.message);
          }
        }),
        catchError(() => {
          return throwError(() => new Error('Booking confirmation failed'));
        })
      );
  }

  public confirmInvoicePayment(bookingId: number) {
    const formData = new FormData();
    formData.append('mode', 'confirmInvoicePayment');
    formData.append('id_booking', bookingId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return response;
          } else {
            throw new Error(response.message);
          }
        }),
        catchError(() => {
          return throwError(() => new Error('Payment confirmation failed'));
        })
      );
  }

  public getAllBookings() {
    const formData = new FormData();
    formData.append('mode', 'getBookings');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: BookingBackendListModel[];
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response =>
          response.data.map(booking => this.parseBackendListModel(booking))
        ),
        catchError(() => {
          return throwError(() => new Error('Booking fetch failed'));
        })
      );
  }

  public getBookingsByUser(userId: number) {
    const formData = new FormData();
    formData.append('mode', 'getBookingsByUser');
    formData.append('id_user', userId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: BookingBackendListModel[];
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response =>
          response.data.map(booking => this.parseBackendListModel(booking))
        ),
        catchError(() => {
          return throwError(() => new Error('Booking fetch failed'));
        })
      );
  }

  public getBookingById(bookingId: number) {
    const formData = new FormData();
    formData.append('mode', 'getBooking');
    formData.append('id_booking', bookingId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: BookingBackendModel;
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => this.parseBackendModel(response.data)),
        catchError(() => {
          return throwError(() => new Error('Booking fetch failed'));
        })
      );
  }

  public getBookingForRevocation(
    request: ContractRevocationRequestModel
  ): Observable<ContractRevocationPreviewModel> {
    const formData = new FormData();
    formData.append('mode', 'getBookingForRevocation');
    formData.append('first_name', request.firstName);
    formData.append('last_name', request.lastName);
    formData.append('email', request.email);
    formData.append('booking_nr', request.bookingNumber);

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: ContractRevocationPreviewBackendModel;
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return this.parseRevocationPreview(response.data);
          }
          throw new Error(response.message);
        }),
        catchError(error =>
          throwError(() => new Error(error.error ?? 'booking_lookup_failed'))
        )
      );
  }

  public revokeContract(
    request: ContractRevocationRequestModel
  ): Observable<{ success: boolean; message: string }> {
    const formData = new FormData();
    formData.append('mode', 'revokeContract');
    formData.append('first_name', request.firstName);
    formData.append('last_name', request.lastName);
    formData.append('email', request.email);
    formData.append('booking_nr', request.bookingNumber);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return response;
          }
          throw new Error(response.message);
        }),
        catchError(error =>
          throwError(
            () => new Error(error.error ?? 'contract_revocation_failed')
          )
        )
      );
  }

  private parseRevocationPreview(
    preview: ContractRevocationPreviewBackendModel
  ): ContractRevocationPreviewModel {
    return {
      bookingId: preview.id,
      bookingNr: preview.booking_nr,
      seminarTitle: preview.seminar_title,
      seminarSeriesName: preview.seminar_series_name,
      seminarStartDate: preview.seminar_start_date
        ? new Date(preview.seminar_start_date)
        : null,
      seminarDates: (preview.seminar_dates ?? [])
        .map(date => ({
          startDate: new Date(date.start_date),
          endDate: new Date(date.end_date),
        }))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime()),
      participants: (preview.attendees ?? []).map(participant => ({
        firstName: participant.first_name,
        lastName: participant.last_name,
        condition: participant.condition,
        conditionPrice: participant.condition_price,
      })),
      coupons: (preview.coupons ?? []).map(coupon =>
        this.couponService.parseCouponCodeBackendModel(coupon)
      ),
      status: preview.status,
      totalPrice: preview.total_price,
      discountedPrice: preview.discounted_total_price,
      paymentStatus: preview.payment_status,
      paymentStatusLabel: preview.payment_status_label,
      isPaid: preview.is_paid,
      cancelOption: preview.cancel_option,
      cancelOptionLabel: preview.cancel_option_label,
      estimatedCancelFee: preview.estimated_cancel_fee,
      cancellationFee: preview.cancellation_fee,
      settlementType: preview.settlement_type,
      settlementAmount: preview.settlement_amount,
      settlementLabel: preview.settlement_label,
      amountDue: preview.amount_due,
      withinRevocationPeriod: preview.within_revocation_period,
    };
  }

  public cancelBooking(bookingId: number, selected_option: string) {
    const formData = new FormData();
    formData.append('mode', 'cancelBooking');
    formData.append('id_booking', bookingId.toString());
    formData.append('cancel_option', selected_option);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'booking.php', formData)
      .pipe(
        map(response => {
          if (response.success) {
            return response;
          } else {
            throw new Error(response.message);
          }
        }),
        catchError(() => {
          return throwError(() => new Error('Booking cancellation failed'));
        })
      );
  }

  public exportBookingsExcel(year?: number) {
    const formData = new FormData();
    formData.append('mode', 'exportBookingsExcel');
    if (year) {
      formData.append('year', year.toString());
    }

    return this.http
      .post(environment.authority_short + 'booking.php', formData, {
        responseType: 'blob',
      })
      .pipe(
        map(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const filename = `Buchungen_${year || new Date().getFullYear()}_${moment().format('YYYY-MM-DD_HHmm')}.csv`;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          return blob;
        }),
        catchError(() => {
          return throwError(() => new Error('Export failed'));
        })
      );
  }

  public exportBookingsExcelPerAttendee(year?: number) {
    const formData = new FormData();
    formData.append('mode', 'exportBookingsExcelPerAttendee');
    if (year) {
      formData.append('year', year.toString());
    }

    return this.http
      .post(environment.authority_short + 'booking.php', formData, {
        responseType: 'blob',
      })
      .pipe(
        map(blob => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const filename = `Buchungen_Pro_Teilnehmer_${year || new Date().getFullYear()}_${moment().format('YYYY-MM-DD_HHmm')}.csv`;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          return blob;
        }),
        catchError(() => {
          return throwError(() => new Error('Export failed'));
        })
      );
  }

  public parseBackendListModel(
    booking: BookingBackendListModel
  ): BookingListModel {
    return {
      id: booking.id,
      bookingNr: booking.booking_nr,
      user: this.userService.parseBackendListModel(booking.user),
      status:
        booking.status === 'pending' &&
        moment(booking.created_at).isBefore(moment().subtract(14, 'days'))
          ? 'late'
          : booking.status,
      totalPrice: booking.total_price,
      discountedPrice: booking.discounted_price,
      attendeeCount: booking.attendee_count,
      seminarName: booking.seminar_name,
      seminarNr: booking.seminar_nr,
      createdAt: booking.created_at ? new Date(booking.created_at) : null,
      updatedAt: booking.updated_at ? new Date(booking.updated_at) : null,
    };
  }

  public parseBackendModel(booking: BookingBackendModel): BookingModel {
    return {
      id: booking.id,
      bookingNr: booking.booking_nr,
      seminar: {
        id: booking.seminar.id,
        title: booking.seminar.title,
        conditions: booking.seminar.conditions,
        category: {
          id: booking.seminar.category.id,
          name: booking.seminar.category.name,
          color50: booking.seminar.category.color50,
          color0: booking.seminar.category.color0,
          color70: booking.seminar.category.color70,
          color80: booking.seminar.category.color80,
          color90: booking.seminar.category.color90,
        },
      },
      seminarDateSeries: {
        id: booking.seminar_date_series.id,
        name: booking.seminar_date_series.name,
        dates: booking.seminar_date_series.dates.map(date => ({
          id: date.id,
          startDate: new Date(date.start_date),
          endDate: new Date(date.end_date),
        })),
      },
      user: this.userService.parseBackendListModel(booking.user),
      participants: booking.attendees?.map(participant => ({
        id: participant.id,
        salutation: participant.salutation || null,
        jobTitle: participant.job_title || null,
        firstName: participant.first_name,
        lastName: participant.last_name,
        email: participant.email,
        phoneNumber: participant.phone_number || null,
        street: participant.street || null,
        streetNr: participant.street_nr || null,
        zip: participant.zip || null,
        city: participant.city || null,
        country: participant.country || null,
        birthdate: moment(participant.birthdate, 'YYYY-MM-DD').toDate(),
        questions: participant.questions || null,
        condition: participant.condition,
        conditionPrice: participant.condition_price,
      })),
      coupons: booking.coupons?.map(coupon =>
        this.couponService.parseCouponCodeBackendModel(coupon)
      ),
      status: booking.status,
      totalPrice: booking.total_price,
      discountedPrice: booking.discounted_total_price,
      invoice: booking.invoice && {
        id: booking.invoice.id,
        invoiceNr: booking.invoice.invoice_nr,
        facility: booking.invoice.facility,
        firstName: booking.invoice.first_name,
        lastName: booking.invoice.last_name,
        email: booking.invoice.email,
        phoneNumber: booking.invoice.phone_number,
        street: booking.invoice.street,
        streetNr: booking.invoice.street_nr,
        zip: booking.invoice.zip,
        city: booking.invoice.city,
        country: booking.invoice.country,
        paymentMethod: booking.invoice.payment_method,
      },
      createdAt: booking.created_at ? new Date(booking.created_at) : null,
      updatedAt: booking.updated_at ? new Date(booking.updated_at) : null,
    };
  }
}
